/**
 * The one definition of what an invoice is owed. Re-exported here because "balance" is a
 * money question and this is where every screen already looks — an issued invoice can carry
 * late fees on top of its frozen total, and `total - paid` quietly under-reports them.
 */
import { ymdToUtcMs } from '@servwell/shared';

export { invoiceBalanceCents } from '@servwell/shared';

/**
 * Money is stored and transported in integer cents — never floats. Format at the edge,
 * here, so no page reinvents it (and no page prints "$12.3").
 */
export function money(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/**
 * The shop's COST BASIS — what it paid — or `null` when this surface may not see it.
 *
 * ⭐ **One gate, so a fifth figure cannot be added past the other four.** The first version
 * of ticket 013 wrote `showCost ? money(x) : null` at each of four call sites, which works
 * and is exactly the shape that leaks the next time someone adds a column: nothing forces a
 * new figure through the check, and no test in a DOM-less suite can see that it skipped it.
 * Routing every cost figure through here makes `money(` itself the thing to look for —
 * `PartsUsedCard` now contains none, and `apps/web/test/field-money.test.ts` says so.
 *
 * ⚠️ This is the shop's side of the boundary ONLY. **What a customer owes is not this** —
 * a tech at the door takes the payment, the method, the cheque number and the tip, and that
 * money renders through `money()` and `balanceLabel()` as it always did.
 *
 * ⚠️ It returns `string | null`, so **never interpolate it into a template literal**:
 * `${null}` is the four visible characters "null". In JSX child position a null renders
 * nothing, which is the whole point. The test guards the interpolation shape.
 */
export function costFigure(
  cents: number | null | undefined,
  showCost: boolean,
): string | null {
  if (!showCost) return null;
  if (cents === null || cents === undefined) return null;
  return money(cents);
}

/**
 * A balance as a person reads it.
 *
 * A negative balance is a real state — the shop waives a late fee the customer already
 * paid, and the money is now theirs — and `money()` renders that as "-$10.00" in a column
 * headed "Balance", which reads as owing minus ten dollars. Say what it means instead.
 * Here rather than per screen, so all four places that print a balance agree.
 */
export function balanceLabel(cents: number): string {
  if (cents < 0) return `${money(-cents)} overpaid`;
  return money(cents);
}

/**
 * Whole dollars in a few characters: "$0", "$500", "$1.5k", "$14k", "$1.4M", "-$50".
 *
 * ⚠️ `minimumFractionDigits: 0` is load-bearing. A currency keeps its two decimals as the
 * MINIMUM, so `maximumFractionDigits: 1` alone prints "$500.0" and "$14.0K" (MAP 220; LESSONS
 * 2026-09-30). Thousands are a lower-case "k", as this file has always promised; `Intl` writes
 * "K".
 */
function compactDollars(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })
    .format(cents / 100)
    .replace(/K$/, 'k');
}

/** Compact form for KPI tiles: $1.2k, $14k, $1.4M; under $1,000 to the cent, "$999.50". */
export function moneyShort(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  if (Math.abs(cents) < 100_000) return money(cents);
  return compactDollars(cents);
}

/**
 * A chart's figure: a gridline's value, or a month below zero said in text where no bar can
 * draw it. Short at every size, "$0", "$500", "$1.5k", "-$50", because it sits in a gutter or
 * under a column; the month's name and tooltip give it to the cent.
 */
export function moneyAxis(cents: number): string {
  return compactDollars(cents);
}

/**
 * Days overdue (negative = still in terms). Null when there's no due date.
 *
 * Both sides are calendar days in the SHOP's zone. This used to build an instant from the
 * due date in the BROWSER's zone and subtract it from `Date.now()`, which made the "Late"
 * badge and the "N days late" line disagree with the server's Overdue count for several
 * hours a day — the same invoice, two numbers, on one screen. A day is not 86,400,000
 * milliseconds either: across a DST change it is 23 or 25 hours, and the subtraction drifted
 * by one twice a year.
 *
 * `ymdToUtcMs` on two `YYYY-MM-DD` strings has neither problem — no zone, no DST, no clock.
 *
 * Takes the shop's `today` rather than its zone, matching `daysOverdue` on the server
 * (`apps/api/src/mcp/reads/_util.ts`) exactly — one name, one contract, both ends of the
 * wire. It also keeps this PURE: resolving the zone in here meant a fresh
 * `Intl.DateTimeFormat` per call, and it is called once per row of a 500-row invoice table
 * that re-renders on every sort and keystroke. Hoist `todayISO(tz)` once per screen.
 */
export function daysOverdue(
  dueDate: string | null | undefined,
  today: string,
): number | null {
  if (!dueDate) return null;
  return Math.floor((ymdToUtcMs(today) - ymdToUtcMs(dueDate)) / 86_400_000);
}

/**
 * How a payment was made, as every screen names it: the office's payments card and the Revenue
 * page's drill under Collected. A method not listed (a new one) prints as it is stored.
 */
export const PAYMENT_METHOD_LABEL: Record<string, string> = {
  cash: 'Cash',
  check: 'Cheque',
  card: 'Card',
  ach: 'Bank transfer',
  other: 'Other',
};
